import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import {
  type PlanActions,
  isChanging,
  isCurrent,
  isDowngrading,
  isRedeemingOffer,
  isRenewing,
  isResuming,
  isUpgrading,
  planActions,
  shouldContactSupport,
} from "@/react/helpers/plansHelpers";
import { usePlanPageVariant } from "@/react/hooks/usePlanPageVariant";
import { usePlanStatus } from "@/react/hooks/usePlanStatus";
import type { Plan } from "@circle-react/types";
import { useUpgradePlanModal } from "@circle-react-shared/UpgradePlan";
import { Button } from "@circle-react-uikit/Button";
import { usePlans } from "./PlansContext";

export interface SubmitButtonProps {
  plan: Plan;
}

export const SubmitButton = ({ plan }: SubmitButtonProps) => {
  const {
    renewSubscription,
    updateSubscription,
    refetchPlans,
    refetchCurrentPlan,
  } = usePlans();

  const history = useHistory();
  const upgradePlanModal = useUpgradePlanModal();
  const { shouldShowProration, isCommunityOnTrial } = usePlanStatus();
  const { isVariantBEnabled } = usePlanPageVariant();

  const getPlanId = () => plan.professional_grandfathered_id || plan.id;

  const onCloseModal = () => {
    refetchPlans();
    refetchCurrentPlan();
  };

  const onChangePlanSuccess = async () => {
    history.push("/feed");
    await upgradePlanModal.hide();
  };

  const shouldShowPlanDetails = isCommunityOnTrial
    ? false
    : !shouldShowProration(plan);

  const showUpgradeModal = async (couponId: any = null) => {
    await upgradePlanModal.show({
      planId: getPlanId(),
      onSuccess: () => onChangePlanSuccess(),
      successButtonLabel: t("pricing_plans.change_plan.go_to_community"),
      showPlanDetails: shouldShowPlanDetails,
      onCloseModal: onCloseModal,
      couponId: couponId,
    });
  };
  const handleChangePlanButtonClick = async () => {
    await showUpgradeModal();
  };
  const handleRedeemOfferButtonClick = async () => {
    await showUpgradeModal(plan.plan_coupon?.id);
  };

  const handleResumeButtonClick = () => {
    updateSubscription(getPlanId());
  };

  const handleRenewButtonClick = () => {
    renewSubscription(getPlanId());
  };

  const handleCurrentButtonClick = () => null;

  const handleContactSupportOnClick = () => {
    window.open("mailto:support@circle.so", "emailWindow");
  };

  const getButtonClickHandler = (buttonString: any) => {
    if (
      isUpgrading(buttonString) ||
      isChanging(buttonString) ||
      isDowngrading(buttonString)
    ) {
      return handleChangePlanButtonClick;
    }
    if (isResuming(buttonString)) {
      return handleResumeButtonClick;
    }
    if (isRenewing(buttonString)) {
      return handleRenewButtonClick;
    }
    if (isCurrent(buttonString)) {
      return handleCurrentButtonClick;
    }
    if (isRedeemingOffer(buttonString)) {
      return handleRedeemOfferButtonClick;
    }
    return undefined;
  };

  const getButtonTranslationKey = (plan_action: PlanActions) => {
    if (
      !isVariantBEnabled &&
      (plan_action === planActions.upgrade ||
        plan_action === planActions.downgrade)
    ) {
      return `pricing_plans.new_plans_page_actions.${plan_action}`;
    }
    return `pricing_plans.actions.${plan_action}`;
  };

  const getButtonVariant = (buttonString: any) => {
    if (isUpgrading(buttonString) || isChanging(buttonString)) {
      return "primary";
    }
    if (
      isResuming(buttonString) ||
      isRenewing(buttonString) ||
      isCurrent(buttonString)
    ) {
      return "circle";
    }
    if (isDowngrading(buttonString)) {
      return "secondary";
    }
    return undefined;
  };

  if (shouldContactSupport(plan.plan_action)) {
    return (
      <button type="button" onClick={handleContactSupportOnClick}>
        {t("pricing_plans.actions.contact_cx")}
      </button>
    );
  }

  const buttonText = t(getButtonTranslationKey(plan.plan_action), {
    plan_name: plan.display_name,
  });

  const buttonBackground =
    plan.plan_action === planActions.upgrade
      ? "bg-gradient-to-tl from-[#471AFF] to-[#509AF0] dark:bg-gradient-to-tl dark:from-white dark:to-white"
      : "";
  return (
    <Button
      variant={getButtonVariant(plan.plan_action)}
      large
      onClick={getButtonClickHandler(plan.plan_action)}
      disabled={isCurrent(plan.plan_action)}
      data-testid={`submit-button-${plan.plan_action}`}
      className={buttonBackground}
    >
      {buttonText}
    </Button>
  );
};
